/**
 * SystemRecordSection - Display matched shipment data from the system
 *
 * This component shows the SYSTEM values (from the matched shipment database record),
 * NOT the extracted values from the BOL. Uses `matched_shipment_data` from the API
 * when available, otherwise falls back to parsing `existing_value` from corrections.
 */

import { formatFieldNameForDisplay, ValidationMetadata } from "@/types/validation";
import { useState } from "react";

interface SystemRecordSectionProps {
  validationMetadata: ValidationMetadata | null;
  shipmentId?: number | null;
  proNumber?: string | null;
  className?: string;
}

interface SystemFieldData {
  value: unknown;
  fieldName: string;
  hasDiscrepancy: boolean;
  severity?: string;
}

/**
 * Format a value for display
 */
function formatDisplayValue(value: unknown, fieldName: string): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-slate-400 italic">Not in system</span>;
  }

  // Format service_level as a colored badge
  if (fieldName === "service_level") {
    const level = String(value).toUpperCase();
    const badgeColors: Record<string, string> = {
      STD: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
      EXP: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400",
      GRD: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
      HOT: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
      DEFAULT: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400",
    };
    const colorClass = badgeColors[level] || badgeColors.DEFAULT;
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${colorClass}`}
      >
        {level}
      </span>
    );
  }

  // Format customer_ref_code as monospace
  if (fieldName === "customer_ref_code") {
    return <span className="font-mono text-sm">{String(value)}</span>;
  }

  // Format currency for rollup_charges
  if (fieldName === "Rollup Charges") {
    const numValue = typeof value === "number" ? value : parseFloat(String(value));
    if (!isNaN(numValue)) {
      return `$${numValue.toFixed(2)}`;
    }
  }

  // Format timestamp fields (actual_pickup_time_local, actual_delivery_time_local)
  if (fieldName === "actual_pickup_time_local" || fieldName === "actual_delivery_time_local") {
    try {
      const dateStr = String(value);
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        // Extract timezone from the string if present (e.g., "2024-01-15 10:30:00-08:00")
        const tzMatch = dateStr.match(/([+-]\d{2}:\d{2})$/);
        const tzSuffix = tzMatch ? ` (UTC${tzMatch[1]})` : "";
        return (
          <div className="space-y-0.5">
            <p className="text-sm font-medium">
              {date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </p>
            <p className="text-xs text-slate-500">
              {date.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              })}
              {tzSuffix}
            </p>
          </div>
        );
      }
    } catch {
      // Fall through to string display
    }
    return String(value);
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  // Handle entity objects (shipper, consignee, bill_to)
  if (typeof value === "object" && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;

    // Carrier object formatting (origin_carrier, destination_carrier)
    if (fieldName === "origin_carrier" || fieldName === "destination_carrier") {
      const name = obj.name as string | null | undefined;
      const scac = obj.ref_scac_id as string | null | undefined;
      const carrierCode = obj.ref_carrier_code as string | null | undefined;
      return (
        <div className="space-y-0.5">
          {name && <p className="text-sm font-medium">{name}</p>}
          <div className="flex items-center gap-2 text-xs">
            {scac && (
              <span className="font-mono bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">
                SCAC: {scac}
              </span>
            )}
            {carrierCode && <span className="text-slate-500">Code: {carrierCode}</span>}
          </div>
        </div>
      );
    }

    // Driver object formatting (pickup_driver, delivery_driver)
    if (fieldName === "pickup_driver" || fieldName === "delivery_driver") {
      const name = obj.name as string | null | undefined;
      const driverCode = obj.ref_driver_code as string | null | undefined;
      return (
        <div className="space-y-0.5">
          {name && <p className="text-sm font-medium">{name}</p>}
          {driverCode && <p className="text-xs font-mono text-slate-500">ID: {driverCode}</p>}
        </div>
      );
    }

    // Address entity formatting
    if ("name" in obj || "address" in obj || "city" in obj) {
      const name = obj.name as string | null | undefined;
      const address = obj.address as string | null | undefined;
      const city = obj.city as string | null | undefined;
      const state = obj.state as string | null | undefined;
      const zipCode = (obj.zip_code ?? obj.zip) as string | null | undefined;
      const refClientCode = obj.ref_client_code as string | null | undefined;

      return (
        <div className="space-y-1">
          {name && <p className="text-sm font-bold">{String(name)}</p>}
          {address && (
            <p className="text-xs text-slate-600 dark:text-slate-400">{String(address)}</p>
          )}
          {(city || state || zipCode) && (
            <p className="text-xs text-slate-500">
              {[city, state, zipCode]
                .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
                .map(String)
                .join(", ")}
            </p>
          )}
          {refClientCode && (
            <p className="text-[10px] text-slate-400 font-mono">
              Account Code: {String(refClientCode)}
            </p>
          )}
        </div>
      );
    }

    // Service center object (origin_sic, destination_sic)
    if (fieldName === "origin_sic" || fieldName === "destination_sic") {
      const code = obj.code as string | null | undefined;
      const name = obj.name as string | null | undefined;
      const city = obj.city as string | null | undefined;
      const state = obj.state as string | null | undefined;
      return (
        <div className="space-y-0.5">
          {code && <p className="text-sm font-bold">{code}</p>}
          {name && <p className="text-xs text-slate-600 dark:text-slate-400">{name}</p>}
          {(city || state) && (
            <p className="text-xs text-slate-500">{[city, state].filter(Boolean).join(", ")}</p>
          )}
        </div>
      );
    }

    // Generic object
    return JSON.stringify(value, null, 2);
  }

  // Handle arrays (e.g., shipment_details, tags, traces)
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-slate-400 italic">None</span>;
    }

    // For shipment details, format nicely with pieces, pallets, weight, class, NMFC, STC
    if (fieldName === "shipment_details") {
      return (
        <div className="space-y-2">
          {value.slice(0, 5).map((item, idx) => {
            const detail = item as Record<string, unknown>;
            const pieces = detail.pieces as number | null | undefined;
            const pallets = detail.pallets as number | null | undefined;
            const weight = detail.weight as number | null | undefined;
            const actualClass = detail.actual_class as string | null | undefined;
            const commodityCode = detail.commodity_code as string | null | undefined;
            const saidToContain = detail.said_to_contain as string | null | undefined;
            const description = detail.description as string | null | undefined;
            return (
              <div key={idx} className="text-xs border-l-2 border-slate-200 pl-2 py-0.5">
                <div className="flex flex-wrap gap-x-2">
                  {pieces != null && <span>{String(pieces)} pcs</span>}
                  {pallets != null && <span>{String(pallets)} plt</span>}
                  {weight != null && <span>{String(weight)} lbs</span>}
                  {actualClass && (
                    <span className="font-medium text-purple-600 dark:text-purple-400">
                      Class {actualClass}
                    </span>
                  )}
                  {commodityCode && (
                    <span className="font-mono text-blue-600 dark:text-blue-400">
                      NMFC: {commodityCode}
                    </span>
                  )}
                </div>
                {saidToContain && (
                  <div className="text-slate-600 dark:text-slate-400 mt-0.5">
                    STC: {saidToContain}
                  </div>
                )}
                {description && <div className="text-slate-500 mt-0.5">{String(description)}</div>}
              </div>
            );
          })}
          {value.length > 5 && (
            <span className="text-xs text-slate-400">+{value.length - 5} more items</span>
          )}
        </div>
      );
    }

    // For traces, show type_code: value pairs
    if (fieldName === "traces") {
      return (
        <div className="space-y-1">
          {value.slice(0, 5).map((item, idx) => {
            const trace = item as Record<string, unknown>;
            const typeCode = trace.type_code as string | null | undefined;
            const traceValue = trace.value as string | null | undefined;
            return (
              <div key={idx} className="text-xs">
                <span className="text-slate-500">{typeCode || "REF"}:</span> {traceValue || "—"}
              </div>
            );
          })}
          {value.length > 5 && (
            <span className="text-xs text-slate-400">+{value.length - 5} more</span>
          )}
        </div>
      );
    }

    // For accessorials, show code: description with charge
    if (fieldName === "accessorials") {
      return (
        <div className="space-y-1">
          {value.slice(0, 5).map((item, idx) => {
            const acc = item as Record<string, unknown>;
            const code = acc.code as string | null | undefined;
            const description = acc.description as string | null | undefined;
            const charge = acc.charge as number | null | undefined;
            return (
              <div key={idx} className="text-xs border-l-2 border-slate-200 pl-2">
                <span className="font-medium">{code || "ACC"}</span>
                {description && <span className="text-slate-500 ml-1">{description}</span>}
                {charge != null && (
                  <span className="text-slate-600 ml-2">${charge.toFixed(2)}</span>
                )}
              </div>
            );
          })}
          {value.length > 5 && (
            <span className="text-xs text-slate-400">+{value.length - 5} more</span>
          )}
        </div>
      );
    }

    // For tags, show name and optional color
    if (fieldName === "tags") {
      const items = value.map((item) => {
        if (typeof item === "string") return item;
        if (typeof item === "object" && item !== null) {
          const obj = item as Record<string, unknown>;
          return String(obj.name ?? JSON.stringify(obj));
        }
        return String(item);
      });
      return items.slice(0, 5).join(", ") + (items.length > 5 ? ` +${items.length - 5} more` : "");
    }

    // For notes, show type_name: text (filter out notes with no text)
    if (fieldName === "notes") {
      const items = value.flatMap((item) => {
        if (typeof item === "string") return item ? [item] : [];
        if (typeof item === "object" && item !== null) {
          const obj = item as Record<string, unknown>;
          const text = obj.text as string | null | undefined;
          if (text) {
            const typeName = obj.type_name as string | null | undefined;
            return [typeName ? `${typeName}: ${text}` : text];
          }
        }
        return [];
      });
      if (items.length === 0) return "No notes";
      return (
        <div className="space-y-1">
          {items.slice(0, 3).map((note, idx) => (
            <div key={idx} className="text-xs border-l-2 border-slate-200 pl-2 py-0.5">
              {note}
            </div>
          ))}
          {items.length > 3 && (
            <span className="text-xs text-slate-400">+{items.length - 3} more</span>
          )}
        </div>
      );
    }

    // For instructions, show code: description
    if (fieldName === "instructions") {
      const items = value.map((item) => {
        if (typeof item === "string") return item;
        if (typeof item === "object" && item !== null) {
          const obj = item as Record<string, unknown>;
          const code = obj.code as string | null | undefined;
          const description = obj.description as string | null | undefined;
          if (code) return description ? `${code}: ${description}` : code;
          return String(description ?? JSON.stringify(obj));
        }
        return String(item);
      });
      return items.slice(0, 5).join(", ") + (items.length > 5 ? ` +${items.length - 5} more` : "");
    }

    return `${value.length} items`;
  }

  return JSON.stringify(value);
}

/**
 * Individual system field display
 */
function SystemField({ data }: { data: SystemFieldData }) {
  const isFullWidth =
    data.fieldName === "shipper" ||
    data.fieldName === "consignee" ||
    data.fieldName === "bill_to" ||
    data.fieldName === "shipment_details" ||
    data.fieldName === "accessorials";

  return (
    <div className={`${isFullWidth ? "col-span-2" : ""}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1">
          <span className="material-symbols-outlined text-xs">database</span>
          {formatFieldNameForDisplay(data.fieldName)}
        </p>
        {data.hasDiscrepancy && (
          <span
            className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
              data.severity === "major"
                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                : data.severity === "moderate"
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  : "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
            }`}
          >
            Differs from BOL
          </span>
        )}
      </div>
      <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
        {formatDisplayValue(data.value, data.fieldName)}
      </div>
    </div>
  );
}

/**
 * Build system record from matched_shipment_data only
 *
 * Note: We no longer fall back to existing_value from corrections - that data
 * is shown in the SuggestedCorrectionsPanel. This section only shows when
 * we have a full matched_shipment_data from the backend.
 */
function buildSystemRecord(validationMetadata: ValidationMetadata): Map<string, SystemFieldData> {
  const systemRecord = new Map<string, SystemFieldData>();

  // Only proceed if we have matched_shipment_data
  const shipmentData = validationMetadata.matched_shipment_data;
  if (!shipmentData || typeof shipmentData !== "object") {
    return systemRecord;
  }

  // Build set of fields with discrepancies from corrections
  const discrepancyFields = new Map<string, { hasDiscrepancy: boolean; severity?: string }>();
  for (const correction of validationMetadata.suggested_corrections || []) {
    discrepancyFields.set(correction.field_name, {
      hasDiscrepancy:
        correction.suggested_action !== "review" ||
        correction.severity === "major" ||
        correction.severity === "moderate",
      severity: correction.severity,
    });
  }

  // Map shipment data fields to display fields - matches backend get_full_shipment_data output
  type ShipmentDataKey = keyof typeof shipmentData;
  const fieldMappings: Array<{ key: ShipmentDataKey; displayName: string }> = [
    { key: "pro_number", displayName: "pro_number" },
    { key: "service_level", displayName: "service_level" },
    { key: "customer_ref_code", displayName: "customer_ref_code" },
    { key: "pickup_date", displayName: "pickup_date" },
    { key: "due_service_date", displayName: "Due Service Date" },
    { key: "origin_sic", displayName: "origin_sic" },
    { key: "destination_sic", displayName: "destination_sic" },
    { key: "origin_carrier", displayName: "origin_carrier" },
    { key: "destination_carrier", displayName: "destination_carrier" },
    { key: "shipper", displayName: "shipper" },
    { key: "consignee", displayName: "consignee" },
    { key: "bill_to", displayName: "bill_to" },
    { key: "pieces", displayName: "pieces" },
    { key: "pallets", displayName: "pallets" },
    { key: "weight", displayName: "weight" },
    { key: "shipment_details", displayName: "shipment_details" },
    { key: "traces", displayName: "traces" },
    { key: "tags", displayName: "tags" },
    { key: "notes", displayName: "notes" },
    { key: "instructions", displayName: "instructions" },
    { key: "accessorials", displayName: "accessorials" },
    { key: "rollup_charges", displayName: "Rollup Charges" },
    { key: "pickup_driver", displayName: "pickup_driver" },
    { key: "delivery_driver", displayName: "delivery_driver" },
    { key: "actual_pickup_time_local", displayName: "actual_pickup_time_local" },
    { key: "actual_delivery_time_local", displayName: "actual_delivery_time_local" },
  ];

  for (const { key, displayName } of fieldMappings) {
    const value = shipmentData[key];
    if (value !== undefined && value !== null) {
      const discrepancy = discrepancyFields.get(displayName) || discrepancyFields.get(key);
      systemRecord.set(displayName, {
        value,
        fieldName: displayName,
        hasDiscrepancy: discrepancy?.hasDiscrepancy || false,
        severity: discrepancy?.severity,
      });
    }
  }

  return systemRecord;
}

export function SystemRecordSection({
  validationMetadata,
  shipmentId,
  proNumber,
  className = "",
}: SystemRecordSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Only show this section when we have actual matched_shipment_data from backend
  const hasMatchedData = validationMetadata?.matched_shipment_data != null;

  // Don't render if no matched shipment data - the SuggestedCorrectionsPanel
  // already shows discrepancy comparisons (extracted vs system) for each field
  if (!validationMetadata || !hasMatchedData) {
    return null;
  }

  const systemRecord = buildSystemRecord(validationMetadata);
  const matchedShipmentId =
    validationMetadata.matched_shipment_data?.shipment_id ??
    (validationMetadata.metadata?.matched_shipment_id as number | null | undefined) ??
    shipmentId;
  const matchedProNumber = validationMetadata.matched_shipment_data?.pro_number ?? proNumber;

  // Define field ordering for display - matches backend get_full_shipment_data output
  const fieldOrder = [
    "pro_number",
    "service_level",
    "customer_ref_code",
    "pickup_date",
    "due_service_date",
    "origin_sic",
    "destination_sic",
    "origin_carrier",
    "destination_carrier",
    "shipper",
    "consignee",
    "bill_to",
    "pieces",
    "pallets",
    "weight",
    "shipment_details",
    "traces",
    "tags",
    "notes",
    "instructions",
    "accessorials",
    "Rollup Charges",
    "pickup_driver",
    "delivery_driver",
    "actual_pickup_time_local",
    "actual_delivery_time_local",
  ];

  // Sort fields by defined order
  const sortedFields = Array.from(systemRecord.entries()).sort(([keyA], [keyB]) => {
    const indexA = fieldOrder.indexOf(keyA);
    const indexB = fieldOrder.indexOf(keyB);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

  return (
    <div className={`rounded-xl border border-slate-200 dark:border-slate-800 ${className}`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">inventory_2</span>
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">Shipment Record</h3>
            {matchedShipmentId && (
              <p className="text-[10px] text-slate-500 font-mono">
                Shipment ID: {matchedShipmentId} {matchedProNumber && `• PRO: ${matchedProNumber}`}
              </p>
            )}
          </div>
        </div>
        <span
          className={`material-symbols-outlined text-slate-400 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
        >
          expand_more
        </span>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-slate-100 dark:border-slate-800 pt-4">
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-4">
            Complete shipment record from the system database. Fields marked &ldquo;Differs from
            BOL&rdquo; have discrepancies with the extracted document data.
          </p>

          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            {sortedFields.map(([fieldName, data]) => (
              <SystemField key={fieldName} data={data} />
            ))}
          </div>

          {sortedFields.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-4">
              No system record data available
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default SystemRecordSection;
